import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  listJadeIdentificationArticles, 
  getJadeIdentificationArticleById,
  createJadeIdentificationArticle, 
  updateJadeIdentificationArticle, 
  deleteJadeIdentificationArticle
} from '../../api/jadeIdentificationArticle';
import { message } from 'antd';

// 分页查询玉石鉴定知识文章列表
export const fetchJadeIdentificationArticles = createAsyncThunk(
  'jadeIdentificationArticle/fetchList',
  async (queryDTO = {}, { rejectWithValue }) => {
    try {
      const response = await listJadeIdentificationArticles(queryDTO);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取文章列表失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 根据ID获取玉石鉴定知识文章详情
export const fetchJadeIdentificationArticleById = createAsyncThunk(
  'jadeIdentificationArticle/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getJadeIdentificationArticleById(id);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取文章详情失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 创建玉石鉴定知识文章
export const createJadeIdentificationArticleAsync = createAsyncThunk(
  'jadeIdentificationArticle/create',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await createJadeIdentificationArticle(data);
      message.success('创建成功');
      // 创建成功后重新获取列表
      const state = getState();
      const { currentPage, pageSize, queryDTO } = state.jadeIdentificationArticle;
      dispatch(fetchJadeIdentificationArticles({
        page: currentPage,
        size: pageSize,
        ...queryDTO
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 更新玉石鉴定知识文章
export const updateJadeIdentificationArticleAsync = createAsyncThunk(
  'jadeIdentificationArticle/update',
  async ({ id, data }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateJadeIdentificationArticle(id, data);
      message.success('更新成功');
      // 更新成功后重新获取列表
      const state = getState();
      const { currentPage, pageSize, queryDTO } = state.jadeIdentificationArticle;
      dispatch(fetchJadeIdentificationArticles({
        page: currentPage,
        size: pageSize,
        ...queryDTO
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 删除玉石鉴定知识文章
export const deleteJadeIdentificationArticleAsync = createAsyncThunk(
  'jadeIdentificationArticle/delete',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await deleteJadeIdentificationArticle(id);
      message.success('删除成功');
      // 删除成功后重新获取列表
      const state = getState();
      const { currentPage, pageSize, queryDTO } = state.jadeIdentificationArticle;
      dispatch(fetchJadeIdentificationArticles({
        page: currentPage,
        size: pageSize,
        ...queryDTO
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const jadeIdentificationArticleSlice = createSlice({
  name: 'jadeIdentificationArticle',
  initialState: {
    articles: [],
    total: 0,
    currentPage: 1,
    pageSize: 10,
    loading: false,
    currentArticle: null,
    queryDTO: {},
    error: null
  },
  reducers: {
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setPageAndSize: (state, action) => {
      state.currentPage = action.payload.page;
      state.pageSize = action.payload.size;
    },
    setCurrentArticle: (state, action) => {
      state.currentArticle = action.payload;
    },
    clearCurrentArticle: (state) => {
      state.currentArticle = null;
    },
    setQueryDTO: (state, action) => {
      state.queryDTO = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 查询列表
    builder.addCase(fetchJadeIdentificationArticles.pending, (state, action) => {
      state.loading = true;
      // 保存查询参数
      if (action.meta?.arg) {
        state.queryDTO = action.meta.arg;
      }
    });
    builder.addCase(fetchJadeIdentificationArticles.fulfilled, (state, action) => {
      state.loading = false;
      state.articles = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.currentPage = action.payload.current || action.payload.page || 1;
    });
    builder.addCase(fetchJadeIdentificationArticles.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取详情
    builder.addCase(fetchJadeIdentificationArticleById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchJadeIdentificationArticleById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentArticle = action.payload;
    });
    builder.addCase(fetchJadeIdentificationArticleById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 创建
    builder.addCase(createJadeIdentificationArticleAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createJadeIdentificationArticleAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createJadeIdentificationArticleAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 更新
    builder.addCase(updateJadeIdentificationArticleAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateJadeIdentificationArticleAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateJadeIdentificationArticleAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 删除
    builder.addCase(deleteJadeIdentificationArticleAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteJadeIdentificationArticleAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteJadeIdentificationArticleAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { setPage, setPageSize, setPageAndSize, setCurrentArticle, clearCurrentArticle, setQueryDTO } = jadeIdentificationArticleSlice.actions;
export default jadeIdentificationArticleSlice.reducer;
